import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { getQuestions, submitQuestionnaire } from '../../api'
import Layout from '../../../../brands/components/Layout'
import { useAppDispatch } from '../../../../../redux/hook'
import {
    HIDE_LOADER,
    SHOW_LOADER,
} from '../../../../../redux/slices/LoaderSlice'
import { SHOW_NOTIFICATION } from '../../../../../redux/slices/showNotificationSlice'
import { Questionnaires, Question } from '../../type'
import './index.css'

const Questionnaire: React.FC = () => {
    const location = useLocation()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [questionnaireData, setQuestionnaireData] =
        useState<Questionnaires | null>(null)
    const [questionnaireId, setQuestionnaireId] = useState<string>('')
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0)
    const [userAnswers, setUserAnswers] = useState<{ [key: string]: string }>(
        {}
    )
    const pathname = location.pathname
    const resourceId = pathname.substring(pathname.lastIndexOf('/') + 1)

    useEffect(() => {
        const fetchData = async () => {
            dispatch(SHOW_LOADER())
            try {
                const response = await getQuestions(resourceId)
                if (response.data.resourceQuestionnaire !== null) {
                    setQuestionnaireData(response.data.resourceQuestionnaire)
                    setQuestionnaireId(
                        response.data.resourceQuestionnaire.questionnaireId ||
                            ''
                    )
                } else {
                    setQuestionnaireData(null)
                }
            } catch (error) {
                console.error(error)
            } finally {
                dispatch(HIDE_LOADER())
            }
        }
        fetchData()
    }, [resourceId, currentQuestionIndex])

    const handleAnswerChange = (questionId: string, answer: string) => {
        setUserAnswers({
            ...userAnswers,
            [questionId]: answer,
        })
    }

    const handleSubmit = async () => {
        dispatch(SHOW_LOADER())
        try {
            const questionResponse = Object.entries(userAnswers).map(
                ([questionId, response]) => ({
                    questionId,
                    response,
                    isCorrect:
                        response ===
                        (questionnaireData?.questionnaireQuestions?.find(
                            (question) => question.questionId === questionId
                        )?.correctAnswer || ''),
                })
            )

            const requestBody = {
                questionnaireId: questionnaireId || '',
                questionResponse,
            }

            const response = await submitQuestionnaire(requestBody)
            if (response.status === 200) {
                dispatch(
                    SHOW_NOTIFICATION({
                        type: 'success',
                        message: 'Your response has been recorded successfully',
                    })
                )
                navigate(-1)
            }

            // Reset userAnswers after successful submission
            setUserAnswers({})
        } catch (error) {
            console.error(error)
        } finally {
            dispatch(HIDE_LOADER())
        }
    }

    const currentQuestion: Question | undefined =
        questionnaireData?.questionnaireQuestions?.[currentQuestionIndex]

    return (
        <div>
            <Layout custom={true}>
                <div className=" ">
                    <div className="mb-3">
                        <div>
                            <h4>
                                {questionnaireData?.questionnaireTitle ||
                                    'Health and Safety Training'}
                            </h4>
                        </div>
                    </div>
                    {currentQuestion && (
                        <>
                            <div className="quiz">
                                <label>{`Question ${currentQuestionIndex + 1}:`}</label>
                                <p>{currentQuestion.questionTitle}</p>
                            </div>
                            <ul className="quiz-list">
                                {currentQuestion.questionOptions.map(
                                    (option, index) => (
                                        <li key={index}>
                                            <label
                                                className={`option-label ${userAnswers[currentQuestion.questionId || ''] === option ? 'selected' : ''}`}
                                            >
                                                <input
                                                    type="radio"
                                                    name={`question-${currentQuestion.questionId}`}
                                                    value={option}
                                                    checked={
                                                        userAnswers[
                                                            currentQuestion.questionId ||
                                                                ''
                                                        ] === option
                                                    }
                                                    onChange={() =>
                                                        handleAnswerChange(
                                                            currentQuestion.questionId ||
                                                                '',
                                                            option
                                                        )
                                                    }
                                                />
                                                <span className="option">{`${String.fromCharCode(65 + index)}) ${option}`}</span>
                                            </label>
                                        </li>
                                    )
                                )}
                            </ul>
                        </>
                    )}

                    <div className="row mt-4 mb-4">
                        <div className="button-block justify-content-between">
                            {currentQuestionIndex > 0 && (
                                <button
                                    className="button button-primary ques_button"
                                    onClick={() =>
                                        setCurrentQuestionIndex((prev) =>
                                            Math.max(prev - 1, 0)
                                        )
                                    }
                                    disabled={currentQuestionIndex === 0}
                                >
                                    PREVIOUS
                                </button>
                            )}

                            {currentQuestionIndex <
                            (questionnaireData?.questionnaireQuestions
                                ?.length || 0) -
                                1 ? (
                                <button
                                    className="button button-primary next-question-button"
                                    onClick={() => {
                                        if (
                                            userAnswers[
                                                currentQuestion?.questionId ||
                                                    ''
                                            ]
                                        ) {
                                            setCurrentQuestionIndex((prev) =>
                                                Math.min(
                                                    prev + 1,
                                                    (questionnaireData
                                                        ?.questionnaireQuestions
                                                        ?.length || 0) - 1
                                                )
                                            )
                                        } else {
                                            dispatch(
                                                SHOW_NOTIFICATION({
                                                    type: 'error',
                                                    message:
                                                        'Please select an option before proceeding',
                                                })
                                            )
                                        }
                                    }}
                                >
                                    NEXT
                                </button>
                            ) : (
                                <button
                                    className="button button-primary ques_button next-question-button"
                                    onClick={handleSubmit}
                                    disabled={
                                        !userAnswers[
                                            currentQuestion?.questionId || ''
                                        ]
                                    }
                                >
                                    SUBMIT
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </Layout>
        </div>
    )
}

export default Questionnaire
